import { getIssuesWithOriginalsAndOptimizedByGroup } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/scope/index.tsx';
import type { IssuesByGroup } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/scope/types.tsx';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types.tsx';
import {
	createSelector,
	createStructuredSelector,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/reselect/index.tsx';
import {
	getDateConfiguration,
	getPlanningUnit,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/plan/index.tsx';
import { getStartDateCustomField } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/custom-fields/index.tsx';
import type { DateConfiguration } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/plan/types.tsx';
import type { CustomField } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/custom-fields/types.tsx';
import type { Issue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/issues/types.tsx';
import { PlanningUnits } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant.tsx';
import { aggregateFunc } from './utils.tsx';
import {
	EARLIEST,
	SUM,
	LATEST,
	type OwnProps,
	type AggregateValue,
	type MapStateToProps,
	type AggregateTypes,
	type AggregateType,
	type ValueType,
	LIST_ALL,
} from './types.tsx';

// Override issue date field value with baseline start/end value
// This is to take inferred/rollup date into consideration
const mapIssueWithBaselineDates = (
	issue: Issue,
	{ baselineStartField, baselineEndField }: DateConfiguration,
	startDateCustomField: CustomField | undefined,
): {
	dueDate: number | null | undefined;
	targetStart: number | null | undefined;
	targetEnd: number | null | undefined;
	[key: string]: number | null | undefined;
} => ({
	dueDate: issue.dueDate,
	targetStart: issue.targetStart,
	targetEnd: issue.targetEnd,
	...(startDateCustomField
		? { [startDateCustomField.id.toString()]: issue.customFields?.[startDateCustomField.id] }
		: {}),
	[baselineStartField.key]: issue.baselineStart,
	[baselineEndField.key]: issue.baselineEnd,
});

export const getAggregatedValuesMapPure = (
	issuesByGroup: IssuesByGroup,
	groupName: string,
	aggregateType: AggregateTypes,
	dateConfiguration: DateConfiguration,
	startDateCustomField: CustomField | undefined,
): AggregateValue | undefined => {
	const group = issuesByGroup[groupName];
	// Get the issues in the group
	if (group && group.issues) {
		// The property key in valuesMap must follow
		let dueDate;
		let targetStart;
		let targetEnd;
		let storyPoints;
		let timeEstimate;
		let startDate;
		const status = [];
		const assignee = [];

		for (const issue of Array.from(group.issues.values())) {
			const safeIssue = mapIssueWithBaselineDates(issue, dateConfiguration, startDateCustomField);
			if (startDateCustomField) {
				startDate = aggregateFunc[aggregateType.startDate](
					startDate,
					safeIssue[startDateCustomField.id.toString()],
				);
			}

			dueDate = aggregateFunc[aggregateType.dueDate](dueDate, safeIssue.dueDate);
			targetStart = aggregateFunc[aggregateType.targetStart](targetStart, safeIssue.targetStart);
			targetEnd = aggregateFunc[aggregateType.targetEnd](targetEnd, safeIssue.targetEnd);
			storyPoints = aggregateFunc[aggregateType.storyPoints](storyPoints, issue.storyPoints);
			timeEstimate = aggregateFunc[aggregateType.timeEstimate](timeEstimate, issue.timeEstimate);

			if (issue.status) {
				status.push(issue.status);
			}

			if (issue.assignee) {
				assignee.push(issue.assignee);
			}
		}

		return {
			dueDate,
			targetStart,
			targetEnd,
			storyPoints,
			timeEstimate,
			status,
			assignee,
			// breakdown(Progress) displays breakdown of issues' statuses
			breakdown: status,
			...(startDateCustomField ? { startDate } : {}),
		};
	}
	return undefined;
};

// Replace the field id to readable field key
const getFieldKey = createSelector(
	[getStartDateCustomField, getPlanningUnit, (_: State, { id }: OwnProps) => id],
	(startDateCustomField, planningUnit, id): string => {
		if (id === startDateCustomField?.id.toString()) {
			return 'startDate';
		}
		if (id === 'estimate') {
			return planningUnit === PlanningUnits.storyPoints ? 'storyPoints' : 'timeEstimate';
		}
		if (id === 'issueStatus') {
			return 'status';
		}
		return id;
	},
);

export const getAggregateTypeMapPure = (
	dateConfiguration: DateConfiguration,
	startDateCustomField: CustomField | undefined,
): { [key: string]: AggregateType } => {
	const { baselineStartField, baselineEndField } = dateConfiguration;

	return {
		// Aggregate by EARLIEST if the field is configured as "start date"
		dueDate: baselineStartField.key === 'dueDate' ? EARLIEST : LATEST,
		// Aggregate by LATEST if the field is configured as "end date"
		targetStart: baselineEndField.key === 'targetStart' ? LATEST : EARLIEST,
		// Aggregate by EARLIEST if the field is configured as "start date"
		targetEnd: baselineStartField.key === 'targetEnd' ? EARLIEST : LATEST,
		// Aggregate by LATEST if the field is configured as "end date"
		startDate: baselineEndField.key === startDateCustomField?.id.toString() ? LATEST : EARLIEST,
		storyPoints: SUM,
		timeEstimate: SUM,
		status: LIST_ALL,
		assignee: LIST_ALL,
	};
};

const getAggregateTypeMap = createSelector(
	[getDateConfiguration, getStartDateCustomField],
	getAggregateTypeMapPure,
);

/**
 * Return an object with values aggregated within the group
 */
const getAggregatedValuesMap = createSelector(
	[
		getIssuesWithOriginalsAndOptimizedByGroup,
		(_: State, { groupName }: OwnProps) => groupName,
		getAggregateTypeMap,
		getDateConfiguration,
		getStartDateCustomField,
	],
	getAggregatedValuesMapPure,
);

const getField = createSelector(
	[getAggregatedValuesMap, getAggregateTypeMap, getFieldKey],
	(
		valuesMap: AggregateValue | undefined,
		aggregateTypes: { [key: string]: AggregateType },
		fieldKey: string,
	): ValueType | undefined => {
		if (valuesMap) {
			switch (fieldKey) {
				case 'status':
				case 'assignee':
				case 'breakdown':
					return {
						value: valuesMap[fieldKey],
						fieldKey,
						type: aggregateTypes[fieldKey],
					};
				case 'startDate':
				case 'dueDate':
				case 'targetStart':
				case 'targetEnd':
				case 'storyPoints':
				case 'timeEstimate':
					return {
						value: valuesMap[fieldKey] ?? undefined,
						fieldKey,
						type: aggregateTypes[fieldKey],
					};
				default:
					return undefined;
			}
		}
		return undefined;
	},
);

const mapStateToProps: MapStateToProps = createStructuredSelector({
	field: getField,
});

export default mapStateToProps;
